import { fabric } from 'fabric';
import { FC } from 'react';
import styled from 'styled-components';
import { editorTheme } from '../../theme';
import { defaultFontFamily } from '../../config';
import { useFabricEditor } from '../../lib/FabricJs';
import { placeNearSlideCenter } from '../../utils/placeNearSlideCenter';
import { disableTextResizeHandles } from '../../utils/textControls';
import Button from './components/Button';
import Span from './components/Span';
import { TextStyle, textStyles } from './textStyles';

enum TextType {
  text = 'text',
  body = 'body',
  subHeading = 'subHeading',
  heading = 'heading',
}

const Text = styled.button`
  background: ${editorTheme.surface};
  border: 1px solid ${editorTheme.border};
  border-radius: 8px;
  color: ${editorTheme.text};
  height: 40px;
  width: 100%;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  cursor: pointer;
  margin-bottom: 8px;
  &:hover {
    border-color: ${editorTheme.accent};
  }
`;

const SubHeading = styled(Text)`
  font-size: 16px;
  height: 48px;
`;

const Heading = styled(Text)`
  font-size: 28px;
  height: 60px;
`;

const StyleSample = styled(Text)`
  height: auto;
  min-height: 48px;
  padding: 8px 12px;
  text-align: left;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const textObjects = {
  text: {
    title: 'Your paragraph text',
    options: {
      fontSize: 32,
      fontFamily: defaultFontFamily,
    },
  },
  body: {
    title: 'Add a little bit of text',
    options: {
      fontSize: 72,
      fontFamily: defaultFontFamily,
    },
  },
  subHeading: {
    title: 'Sub Heading',
    options: {
      fontSize: 96,
      fontFamily: defaultFontFamily,
    },
  },
  heading: {
    title: 'Heading',
    options: {
      fontSize: 160,
      fontFamily: defaultFontFamily,
    },
  },
};

const TextWidget: FC = () => {
  const { canvas } = useFabricEditor();
  const addTextObject = (text: string, options: fabric.ITextOptions) => {
    if (!canvas) return;
    const newText = new fabric.IText(text, options);
    placeNearSlideCenter(canvas, newText);
    disableTextResizeHandles(newText);
    canvas.add(newText);
  };
  const addText = (type: TextType) => {
    const textObject = textObjects[type];
    if (textObject) addTextObject(textObject.title, textObject.options);
  };
  // Text is measured with the font it uses, so the font is loaded first.
  const addStyledText = async ({ text, options }: TextStyle) => {
    const { fontFamily = defaultFontFamily, fontWeight = 'normal' } = options;
    await document.fonts
      .load(`${fontWeight} 16px "${fontFamily}"`)
      .catch(() => undefined);
    if (canvas?.getContext()) addTextObject(text, options);
  };
  return (
    <>
      <Button onClick={() => addText(TextType.text)}>Add a text box</Button>
      <Span>Click text to add to Page</Span>
      <Heading onClick={() => addText(TextType.heading)}>Add a heading</Heading>
      <SubHeading onClick={() => addText(TextType.subHeading)}>
        Add a sub heading
      </SubHeading>
      <Text onClick={() => addText(TextType.body)}>
        Add a little bit of body text
      </Text>
      <Span>Text styles</Span>
      {textStyles.map((style) => (
        <StyleSample
          key={style.name}
          aria-label={`Add ${style.name} text`}
          title={style.name}
          style={{
            fontFamily: style.options.fontFamily,
            fontWeight: style.options.fontWeight,
            fontSize: style.previewSize,
            letterSpacing: style.options.charSpacing
              ? `${style.options.charSpacing / 1000}em`
              : undefined,
          }}
          onClick={() => addStyledText(style)}
        >
          {style.text}
        </StyleSample>
      ))}
    </>
  );
};

export default TextWidget;
